import clsx from "clsx"
import {
  forwardRef,
  type ReactNode,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { flushSync } from "react-dom"

import styles from "./DragPreview.module.css"

export type DragPreviewRenderer = (
  draggedKey: string,
  callback: (element: HTMLElement) => void,
) => void

export interface DragPreviewProps {
  direction: "horizontal" | "vertical"
  children: (key: string) => ReactNode
}

/*
 * An HTML5 Drag & drop preview MUST be in the document at the time setDragImage is called.
 * This component ensures it's there but invisible then immediately removed after the copy.
 */
export const DragPreview = forwardRef<DragPreviewRenderer, DragPreviewProps>(
  function DragPreview(props, ref) {
    const { direction, children: render } = props

    const [children, setChildren] = useState<ReactNode>()
    const domRef = useRef(null)

    useImperativeHandle(
      ref,
      () => (draggedKey: string, callback: (node: HTMLElement) => void) => {
        // This will be called during onDragStart. We need to render the
        // preview synchronously before this event returns so we can call event.dataTransfer.setDragImage.
        flushSync(() => {
          setChildren(render(draggedKey))
        })

        if (domRef.current) callback(domRef.current)

        // Remove the preview from the DOM after a frame so the browser has time to paint.
        requestAnimationFrame(() => {
          setChildren(undefined)
        })
      },
      [render],
    )

    if (!children) return null

    return (
      <div
        className={clsx(styles.dragPreview, styles[`direction-${direction}`])}
        ref={domRef}
      >
        {children}
      </div>
    )
  },
)
